import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { Modal, Typography } from '@getsynapse/design-system';
import { updateRequest } from 'state/ActiveRequest/activeRequestSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { getRequestPermissions } from 'state/normalized/requestPermissions/requestPermissionsSlice';

type ArchiveRequestModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  requestId: string;
};

const ArchiveRequestModal = ({
  isOpen,
  setIsOpen,
  requestId,
}: ArchiveRequestModalProps) => {
  const dispatch = useDispatch();

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleArchiveRequest = async () => {
    handleClose();
    try {
      await dispatch(
        updateRequest({
          id: requestId,
          changes: { isArchived: true },
          isArchiving: true,
        })
      );
      await dispatch(getRequestPermissions({ requestIds: [requestId] }));
    } catch (error) {
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.ARCHIVE_ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.ARCHIVE_ERROR_MESSAGE'
          ),
          autoHide: false,
        })
      );
    }
  };

  return (
    <Modal
      aria-label={intl.get('REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.TITLE')}
      closeModal={handleClose}
      isOpen={isOpen}
      title={intl.get('REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.TITLE')}
      size='small'
      data-testid='archive-request-modal'
      titleIcon={{
        name: 'archive',
        className: 'text-primary-dark h-5 w-5',
      }}
      actionButtons={[
        {
          children: intl.get(
            'REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.ARCHIVE_BUTTON'
          ),
          onClick: handleArchiveRequest,
          variant: 'primary',
          'data-cy': 'modal-archive-request-button',
          className: 'h-10',
        },
        {
          children: intl.get('CANCEL'),
          onClick: handleClose,
          variant: 'tertiary',
          'data-cy': 'cancel-request-button',
          className: 'h-10',
        },
      ]}
      childrenClassName='p-0'
    >
      <Typography className='mb-6'>
        {intl.get('REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.BODY_FIRST_LINE')}
      </Typography>
      <Typography>
        {intl.get(
          'REQUEST_PAGE.TOP_BAR.ARCHIVE_REQUEST_MODAL.BODY_SECOND_LINE'
        )}
      </Typography>
    </Modal>
  );
};

export default ArchiveRequestModal;
